import { defineStore } from 'pinia';
import { axios } from '@boot/axios.js';
import { getCurrentTimeInTimezone, isShiftActive } from '@utils/time.js';
/**
 * Return the default state for the facilities store.
 */
function getDefaultState() {
    return {
        shifts: {},
        facilities: null,
        facilityGroups: {},
        selectedFacility: null,
        failedAssetLoads: {}
    };
}
/**
 * Store for managing facility-related data.
 */
export const useFacilitiesStore = defineStore('facilities', {
    state: () => getDefaultState(),
    getters: {
        /**
         * Get shifts for a specific facility.
         */
        getShiftsByFacility: state => {
            return (facilityId) => {
                const id = Number(facilityId);
                if (id in state.shifts) {
                    return state.shifts[id];
                }
                return null;
            };
        },
        /**
         * Check if there's an active shift for the current time for a facility.
         */
        hasActiveShift: state => {
            return (facilityId, facilityTimezone = 'UTC') => {
                const id = Number(facilityId);
                if (!(id in state.shifts)) {
                    return null; // Shifts not yet loaded
                }
                const shifts = state.shifts[id];
                if (shifts.length === 0) {
                    return false; // No shifts defined
                }
                // Get current time and day in the facility's timezone
                const { currentTime, currentDayKey } = getCurrentTimeInTimezone(facilityTimezone);
                // Check if any shift is active for current day and time
                for (const shift of shifts) {
                    if (isShiftActive(shift, currentTime, currentDayKey)) {
                        return true;
                    }
                }
                return false; // No active shift found
            };
        },
        /**
         * Get a single facility by id.
         */
        getFacility: state => {
            return (id) => {
                const facilityId = Number(id);
                if (state.facilities && state.facilities[facilityId]) {
                    return state.facilities[facilityId];
                }
                return null;
            };
        },
        /**
         * Get a single facility group by id.
         */
        getFacilityGroup: state => {
            return (id) => {
                const groupId = Number(id);
                if (state.facilityGroups && state.facilityGroups[groupId]) {
                    return state.facilityGroups[groupId];
                }
                return null;
            };
        },
        /**
         * Get the current facility selected.
         */
        getSelectedFacility: state => {
            if (state.selectedFacility &&
                state.facilities &&
                state.facilities[state.selectedFacility]) {
                return state.facilities[state.selectedFacility];
            }
            return null;
        },
        /**
         * Get list of facilities.
         */
        getFacilities: state => {
            if (state.facilities === null) {
                return null;
            }
            else {
                return Object.values(state.facilities).sort((a, b) => a.name.localeCompare(b.name));
            }
        },
        /**
         * Get list of facility groups that have facilities.
         */
        getFacilityGroups: state => {
            if (!state.facilityGroups ||
                Object.keys(state.facilityGroups).length === 0) {
                return [];
            }
            else {
                return Object.values(state.facilityGroups)
                    .filter(group => group.facilities)
                    .sort((a, b) => a.name.localeCompare(b.name));
            }
        },
        /**
         * Check if a request to load assets for a facility has already been attempted.
         */
        checkAssetsLoadingFailed: state => {
            return (facilityId) => {
                return Number(facilityId) in state.failedAssetLoads;
            };
        }
    },
    actions: {
        /**
         * Reset store state.
         */
        resetState() {
            this.$reset();
        },
        /**
         * Load shifts for a facility from server.
         */
        async loadShiftsAction(facilityId) {
            try {
                const response = await axios.get(`/facilities/${facilityId}/shifts/`);
                const shifts = response.data.results;
                // Sort shifts by start time
                shifts.sort((a, b) => a.start_hour.localeCompare(b.start_hour));
                // Store shifts
                const id = Number(facilityId);
                this.shifts[id] = shifts;
                return this.shifts[id];
            }
            catch (error) {
                console.error('Failed to load shifts due to: ', error);
                throw error;
            }
        },
        /**
         * Load facilities from server and set them into the store.
         */
        async loadFacilitiesAction() {
            try {
                const response = await axios.get('facilities/');
                const facilities = response.data.results;
                // Initialize facilities object if null
                if (this.facilities === null) {
                    this.facilities = {};
                }
                // Add facilities to store
                for (const facility of facilities) {
                    this.facilities[facility.id] = { ...facility };
                }
                return facilities;
            }
            catch (error) {
                console.error('Failed to load facilities due to: ', error);
                throw error;
            }
        },
        /**
         * Load facility groups from server and set them into the store.
         */
        async loadFacilityGroupsAction() {
            try {
                const response = await axios.get('facility_groups/');
                const rawData = response.data.results;
                const facilityGroups = rawData.map((group) => ({
                    id: group.id,
                    name: group.name,
                    facilities: group.facilities
                }));
                // Add facility groups to store
                for (const group of facilityGroups) {
                    this.facilityGroups[group.id] = { ...group };
                }
                return facilityGroups;
            }
            catch (error) {
                console.error('Failed to load facility groups due to: ', error);
                throw error;
            }
        },
        /**
         * Update facility.
         */
        async updateFacilityAction(facility) {
            try {
                await axios.put(`facilities/${facility.id}/`, facility);
                // Update in store
                if (this.facilities) {
                    this.facilities[facility.id] = { ...facility };
                }
            }
            catch (error) {
                console.error('Failed to update facility due to: ', error);
                throw error;
            }
        },
        /**
         * Select the current facility that is active for the store.
         */
        selectFacilityAction(facilityId) {
            this.selectedFacility = Number(facilityId);
        },
        /**
         * Clear the selected facility.
         */
        clearSelectedFacilityAction() {
            this.selectedFacility = null;
        },
        /**
         * Add a facility ID for a load assets request that has failed.
         */
        addLoadingAssetsFailedAction(facilityId) {
            const id = Number(facilityId);
            this.failedAssetLoads[id] = id;
        },
        /**
         * Remove a facility ID for a load assets request that has failed.
         */
        removeLoadingAssetsFailedAction(facilityId) {
            const id = Number(facilityId);
            delete this.failedAssetLoads[id];
        },
        /**
         * Add a shift to the store (optimistic update).
         * @param {number|string} facilityId - The facility id.
         * @param {Object} shift - The shift object to add.
         */
        addShiftToStoreAction(facilityId, shift) {
            const id = Number(facilityId);
            if (!(id in this.shifts)) {
                this.shifts[id] = [];
            }
            this.shifts[id].push(shift);
            // Sort shifts by start time
            this.shifts[id].sort((a, b) => a.start_hour.localeCompare(b.start_hour));
        },
        /**
         * Update a shift in the store (optimistic update).
         * @param {number|string} facilityId - The facility id.
         * @param {Object} updatedShift - The updated shift object.
         */
        updateShiftInStoreAction(facilityId, updatedShift) {
            const id = Number(facilityId);
            if (id in this.shifts) {
                const index = this.shifts[id].findIndex(shift => {
                    // Match by id for saved shifts, tempId for temporary shifts
                    if (updatedShift.id) {
                        return shift.id === updatedShift.id;
                    }
                    else {
                        return (shift.tempId ===
                            updatedShift.tempId);
                    }
                });
                if (index !== -1) {
                    this.shifts[id][index] = updatedShift;
                    // Sort shifts by start time
                    this.shifts[id].sort((a, b) => a.start_hour.localeCompare(b.start_hour));
                }
            }
        },
        /**
         * Remove a shift from the store (optimistic update).
         * @param {number|string} facilityId - The facility id.
         * @param {number|string} shiftId - The shift id to remove (or tempId if isTemp=true).
         * @param {boolean} isTemp - If true, removes by tempId instead of id.
         */
        removeShiftFromStoreAction(facilityId, shiftId, isTemp = false) {
            const id = Number(facilityId);
            if (id in this.shifts) {
                this.shifts[id] = this.shifts[id].filter(shift => {
                    if (isTemp) {
                        return (shift.tempId !==
                            shiftId);
                    }
                    return shift.id !== Number(shiftId);
                });
            }
        }
    }
});
